import React, { useEffect, useState, useRef } from 'react';
import { Icon } from '@iconify/react';
import $ from 'jquery';
import Select from 'react-select';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import { getMeters, createMeter, updateMeter, deleteMeter } from '../api/metersAPI';
import { getMeasurementUnits } from '../api/measurement-unitsAPI';
import { fetchData } from '../utils/apiHelpers';

const MetersLayer = () => {
  const userProfile = useUserProfile();
  const [meterList, setMeterList] = useState([]);
  const [unitList, setUnitList] = useState([]);
  const [newMeter, setNewMeter] = useState({ negativeValues: 0, cumulative: 0, places: 0 });
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});

  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const unitOptions = unitList.map(u => ({
    value: u.id,
    label: `${u.unitName} (${u.unitCode})`
  }));

  const fetchMeters = () => {
    fetchData(getMeters, {}, setMeterList);
  };

  const fetchUnits = () => {
    fetchData(getMeasurementUnits, {}, setUnitList);
  };

  useEffect(() => {
    fetchMeters();
    fetchUnits();
  }, []);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
    if (meterList.length > 0) {
      dtRef.current = $(tableRef.current).DataTable({
        pageLength: 25,
        dom: 'rtp',
        responsive: false,
        language: {
          paginate: { first: '', last: '', next: '', previous: '' },
        },
      });
    }
    return () => {
      if (dtRef.current) {
        dtRef.current.destroy(true);
        dtRef.current = null;
      }
    };
  }, [meterList]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.search(searchTerm).draw();
    }
  }, [searchTerm]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewMeter(prev => ({ ...prev, [name]: value }));
  };

  const handleEdit = (item) => {
    const rule = Array.isArray(item.rules) ? item.rules[0] : item.rules || {};
    setNewMeter({
      name: item.name,
      measurementUnitsId: item.measurementUnitsId,
      period: rule.period,
      maxValue: rule.maxValue,
      minValue: rule.minValue,
      negativeValues: rule.negativeValues ? 1 : 0,
      cumulative: rule.cumulative ? 1 : 0,
      places: rule.places,
    });
    setEditingId(item.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this meter?')) return;
    try {
      await deleteMeter(id);
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
      await fetchMeters();
    } catch (error) {
      console.error('Error deleting meter:', error);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!newMeter.name?.trim()) newErrors.name = 'Meter name is required';
    if (!newMeter.measurementUnitsId) newErrors.measurementUnitsId = 'Unit is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    const payload = {
      name: newMeter.name,
      measurementUnitsId: newMeter.measurementUnitsId,
      rules: JSON.stringify([
        {
          period: newMeter.period ? parseInt(newMeter.period, 10) : 1,
          places: newMeter.places ? parseInt(newMeter.places, 10) : 0,
          maxValue: newMeter.maxValue ? parseInt(newMeter.maxValue, 10) : 99999,
          minValue: newMeter.minValue ? parseInt(newMeter.minValue, 10) : 0,
          cumulative: Boolean(newMeter.cumulative),
          negativeValues: Boolean(newMeter.negativeValues),
        },
      ]),
    };

    try {
      if (editingId) {
        await updateMeter(editingId, payload);
      } else {
        await createMeter(payload);
      }

      setShowModal(false);
      setEditingId(null);
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
      setNewMeter({ negativeValues: 0, cumulative: 0, places: 0 });
      await fetchMeters();
    } catch (error) {
      console.error('Error saving meter:', error);
    }
  };

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex justify-content-between align-items-center p-3">
        <input
          type="text"
          className="form-control w-auto"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {userProfile?.privileges?.includes('meters:create') && (
          <button
            className="btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2"
            onClick={() => {
              setNewMeter({ negativeValues: 0, cumulative: 0, places: 0 });
              setEditingId(null);
              setShowModal(true);
            }}
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl line-height-1" />
            Add Meter
          </button>
        )}
      </div>

      <div className="card-body p-0">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table mb-0 w-100 rounded-0" ref={tableRef}>
            <thead>
              <tr>
                <th className="rounded-0">Name</th>
                <th>Unit</th>
                <th>Period</th>
                <th>Max Value</th>
                <th>Min Value</th>
                <th>Negative Values</th>
                <th>Cumulative</th>
                <th>Places</th>
                <th className="rounded-0">Actions</th>
              </tr>
            </thead>
            <tbody>
              {meterList.map((item) => {
                const rule = Array.isArray(item.rules) ? item.rules[0] : item.rules || {};
                return (
                  <tr key={item.id}>
                    <td>{item.name}</td>
                    <td>
                      {item.measurementUnits?.unitName} ({item.measurementUnits?.unitCode})
                    </td>
                    <td>{rule.period}</td>
                    <td>{rule.maxValue}</td>
                    <td>{rule.minValue}</td>
                    <td>
                      <div className="form-switch switch-primary d-flex align-items-center gap-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          checked={rule.negativeValues === true || rule.negativeValues === 1}
                          disabled
                        />
                      </div>
                    </td>
                    <td>
                      <div className="form-switch switch-primary d-flex align-items-center gap-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          checked={rule.cumulative === true || rule.cumulative === 1}
                          disabled
                        />
                      </div>
                    </td>
                    <td>{rule.places}</td>
                    <td>
                      <div className="d-flex gap-2">
                        {userProfile?.privileges?.includes('meters:update') && (
                          <button
                            className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                            onClick={() => handleEdit(item)}
                          >
                            <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                          </button>
                        )}
                        {userProfile?.privileges?.includes('meters:delete') && (
                          <button
                            className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                            onClick={() => handleDelete(item.id)}
                          >
                            <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Meter' : 'New Meter'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Meter Name *</label>
                    <input
                      className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                      name="name"
                      value={newMeter.name || ''}
                      onChange={handleInputChange}
                    />
                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Measurement Unit *</label>
                    <Select
                      name="measurementUnitsId"
                      classNamePrefix={`react-select ${errors.measurementUnitsId ? 'is-invalid' : ''}`}
                      value={unitOptions.find(opt => opt.value === newMeter.measurementUnitsId) || null}
                      onChange={(opt) => setNewMeter(prev => ({ ...prev, measurementUnitsId: opt?.value || null }))}
                      options={unitOptions}
                      isClearable
                      placeholder="Select Unit"
                    />
                    {errors.measurementUnitsId && <div className="react-select-invalid">{errors.measurementUnitsId}</div>}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Period in Days</label>
                    <input
                      type="number"
                      name="period"
                      className="form-control"
                      value={newMeter.period || ''}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Max Value</label>
                    <input
                      type="number"
                      name="maxValue"
                      className="form-control"
                      value={newMeter.maxValue || ''}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Min Value</label>
                    <input
                      type="number"
                      name="minValue"
                      className="form-control"
                      value={newMeter.minValue || ''}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Allow Negative Values</label>
                    <div className="form-switch switch-primary d-flex align-items-center gap-3 mt-10">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        checked={Boolean(newMeter.negativeValues)}
                        onChange={(e) =>
                          setNewMeter(prev => ({ ...prev, negativeValues: e.target.checked ? 1 : 0 }))
                        }
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Cumulative</label>
                    <div className="form-switch switch-primary d-flex align-items-center gap-3 mt-10">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        checked={Boolean(newMeter.cumulative)}
                        onChange={(e) =>
                          setNewMeter(prev => ({ ...prev, cumulative: e.target.checked ? 1 : 0 }))
                        }
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Decimal Places</label>
                    <input
                      type="number"
                      name="places"
                      min="0"
                      max="5"
                      className="form-control"
                      value={newMeter.places ?? ''}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Close
                </button>
                <button className="btn btn-primary" onClick={handleSave}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MetersLayer;
